import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { IOnOff } from '../../interfaces/on-off.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { DRLCClientPlugin } from '../drlc/drlc-plugin-client';

export class OnOffPlugin extends SmartenitPlugin implements IOnOff {
  private _timerEnabled: number = 0;
  private _timerDurationInSeconds: number = 0;

  public on(): void {
    this.state = true;
    return this.executeMethod(this.componentId, this.processorName, 'On', null);
  }

  public off(): void {
    this.state = false;
    return this.executeMethod(
      this.componentId,
      this.processorName,
      'Off',
      null
    );
  }

  public getReportableAttrs() {
    return ['OnOff', 'Mode', 'RunningTimer'];
  }

  public setTimerEnabled(modeValue: number): void {
    this._timerEnabled = modeValue;
    const params = { value: modeValue };
    this.device
      .setAttribute(this.componentId, this.processorName, 'Mode', params, false)
      .subscribe();
    this._onUpdate.next({ compNumber: this.componentId, _timerEnabled: this._timerEnabled });
  }

  public updateTimerEnabled(): void {
    this.device
      .getAttribute(
        this.componentId,
        this.processorName,
        'Mode',
        undefined,
        'cached=false'
      )
      .subscribe();
  }

  public setTimer(timeInSeconds: number) {
    this._timerDurationInSeconds = timeInSeconds;
    const params = { value: timeInSeconds };
    this.device
      .setAttribute(this.componentId, this.processorName, 'DefaultTimer', params, false)
      .subscribe();
    this._onUpdate.next({ compNumber: this.componentId, _timerDurationInSeconds: this._timerDurationInSeconds });
  }

  public updateTimer(): void {
    this.device
      .getAttribute(
        this.componentId,
        this.processorName,
        'DefaultTimer',
        undefined,
        'cached=false'
      )
      .subscribe();
  }

  public toggle(): void {
    // return this.executeMethod(this.componentId, this.processorName, 'Toggle', null);
    if (this.isOn()) {
      this.off();
    } else {
      this.on();
    }
  }

  public isOn(): boolean {
    return this.state === true;
  }

  public isOff(): boolean {
    return this.state === false;
  }

  public isTimerEnabled(): number {
    return this._timerEnabled;
  }

  public getTimerDurationInSeconds(): number {
    return this._timerDurationInSeconds;
  }

  public getStatusPayload(): any {
    return ['OnOff'];
  }

  public getEffectOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_ON',
            ''
          )
        ),
        name: 'turn On',
        value: {
          method: 'On'
        }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_OFF',
            ''
          )
        ),
        name: 'turn Off',
        value: {
          method: 'Off'
        }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TOGGLE',
            ''
          )
        ),
        name: 'Toggle',
        value: {
          method: 'Toggle'
        }
      }
    ];
  }

  public getResponderOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_ON',
            ''
          )
        ),
        name: 'turn On',
        value: true
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_OFF',
            ''
          )
        ),
        name: 'turn Off',
        value: false
      }
    ];
  }

  public getConditionOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_ON',
            ''
          )
        ),
        name: 'is On',
        value: 1
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_OFF',
            ''
          )
        ),
        name: 'is Off',
        value: 0
      }
    ];
  }

  public getEffectAttribute(): string {
    return 'null';
  }

  public getEffectMethod(context?: any): string | null {
    if (!context) {
      return null;
    }

    const currentMethod = context.method;
    if (['On', 'Off', 'Toggle'].indexOf(currentMethod) > -1) {
      return currentMethod;
    }

    return null;
  }

  public getConditionAttribute(): string {
    return 'OnOff';
  }

  public getResponderAttribute(): string {
    return 'OnOff';
  }

  public getResponderMethod(): string | null {
    return null;
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;
    if (attribute === 'state' || attribute === 'OnOff') {
      const response = message && message.data && message.data.response;

      this.state =
        response.value === 'On' ||
        response.value === true ||
        response.value === 1;

      this._onUpdate.next({ state: this.state });
      if (this.device.supportsDRLC) {
        const procesor = this.device.getProcessor('DRLCClient');
        const plugin: DRLCClientPlugin = (<DRLCClientPlugin> this.device.getPlugin(procesor.componentId, 'DRLCClient'));
        if (plugin) {
          const sub = plugin.getAttributeWithSubscribeOption(procesor.componentId, 'DRLCClient', 'EventStatus',
            { cached: false, timeout: 1 }, true);
          if (sub) {
            sub.subscribe((d) => {
              if (d && Array.isArray(d.data) && d.data.length > 0) {
                plugin.triggerDRLCEvent(d.data[0].v);
              }
            });
          }
        }
      }
    }

    if (attribute === 'DefaultTimer') {
      const response = message && message.data && message.data.response;
      const _value = parseInt(response.value, 10);
      if (!isNaN(_value)) {
        this._timerDurationInSeconds =
          response.value;

        this._onUpdate.next({ compNumber: this.componentId, _timerDurationInSeconds: this._timerDurationInSeconds });
      }
    }

    if (attribute === 'Mode') {
      const response = message && message.data && message.data.response;
      this._timerEnabled = response.value;
      this._onUpdate.next({ compNumber: this.componentId, _timerEnabled: this._timerEnabled });
    }

    return this.state;
  }
}
